import styles from "./Title.module.scss";
import { OwnBrand } from "../../utils/product/getOwnBrand";
import { ElementType, useEffect } from "react";
import classNames from "classnames/bind";
import { ESIInclude } from "../__shared__/ESIInclude/ESIInclude";
import { FeatureFlags } from "@/types/types";
import { useRouter } from "next/router";

const cx = classNames.bind(styles);
type Props = {
  /**
   * The title of the product.
   */
  title: string;
  /**
   * The own brand of the product.
   */
  ownBrand: OwnBrand;
  /**
   * Any other brand of the product.
   */
  otherBrand: string;
  /**
   * Used to override the element used to render the component.
   */
  element?: ElementType;
  /**
   * Used to remove h1 margin for beauty simplification work
   */
  simplifiedBeautyTitle?: boolean;
  flags: FeatureFlags;
  /**
   * A string representing the product ID from the Product Information Management system.
   */
  productId: string;
  /**
   * A string representing the SKU ID of the product.
   */
  skuId: string;
  /**
   * Boolean that represents if the product is multisku
   */
  isMultiSku: boolean;
};

export function TitleUI({
  title,
  ownBrand,
  otherBrand,
  element = "h1",
  simplifiedBeautyTitle = false,
  flags,
  productId,
  skuId,
  isMultiSku,
}: Props) {
  const Component = element;
  const router = useRouter();

  const headerClassNames = cx(styles.title, {
    simplifiedBeautyTitle: simplifiedBeautyTitle,
  });

  const showWishlistIconButton = !!flags.showWishlistIconButton.value;

  useEffect(() => {
    const isCustomEvent = (event: Event): event is CustomEvent => {
      return "detail" in event;
    };

    if (!showWishlistIconButton) {
      return;
    }

    document.body.addEventListener("dataStateChange", (e: Event) => {
      if (isCustomEvent(e)) {
        if (!e.detail) {
          return;
        }

        const { type, subType, currentSkuUrl } = e.detail;

        if (
          type === "pdp - wishlist" &&
          subType === "modal - choose variant" &&
          currentSkuUrl
        ) {
          router.push(currentSkuUrl, undefined, {
            shallow: true,
            scroll: false,
          });
        }
      } else {
        return;
      }
    });
  }, []);

  return (
    <>
      <Component
        data-testid="product:title"
        className={headerClassNames}
        data-component="Title"
      >
        <div>
          {ownBrand === "jl_own_brand_anyday_legacy" && (
            <a className={styles.logoAnchor} href="/">
              <span
                data-testid="product:title:anyday"
                className={styles.anydayLogo}
              >
                ANYDAY
              </span>
            </a>
          )}

          {ownBrand === "jl_own_brand_anyday" && (
            <a className={styles.logoAnchor} href="/">
              <span
                data-testid="product:title:anyday"
                className={styles.anydayLogo}
              >
                <span className={styles.titlePrefix}>{`John Lewis `}</span>
                ANYDAY
              </span>
            </a>
          )}

          {ownBrand === "jl_own_brand_john_lewis" && (
            <span
              data-testid="product:title:johnlewis"
              className={styles.johnLewisBrand}
            >
              John Lewis
            </span>
          )}

          {!ownBrand && otherBrand && (
            <span
              data-testid="product:title:otherBrand"
              className={styles.otherBrand}
            >
              {otherBrand}
            </span>
          )}

          {title}
        </div>
        {showWishlistIconButton ? (
          <span className={styles.wishlist}>
            <span
              id="add-to-wish-list-esi"
              data-test-id="wish-list-esi"
              data-mode="icon-placeholder-pdp"
            >
              <ESIInclude
                id="add-to-wishlist-component"
                src="/add-to-wishlist/exclude-cpa.html"
              />
            </span>
            <div
              data-testid="wish-list-esi-button"
              data-wishlist-placeholder
              data-product-id={productId}
              data-product-skuid={skuId}
              data-product-multi-sku={isMultiSku}
              className={styles.wishListButton}
            ></div>
          </span>
        ) : null}
      </Component>
    </>
  );
}
